import { Action } from 'redux';

import * as actions from './actions';
import { NewInstance } from '@common/types/data/new-instance';
import SimpleArtist from '@common/types/artist/simple-artist';

import { ErrorAction } from '../common/error-action';
import { LoadingAction } from '../common/loading-action';
import { ArtistPage } from '@services/artist/types/artist-page';
import { ArtistPageSection } from '@services/artist/types/artist-page/sections';
import { ArtistSearchResponse } from '@services/artist/types/artist-search-response';
import SimpleUser from '@common/types/user/simple-user';
import { InviteArtistRequest } from '@common/types/invites/invite-artist-request';
import { ValidType } from '../../../../../services/collaborator/types/valid-types';
import { ValidAssetType } from '../../../../../services/collaborator/types/valid-asset-types';
import Achievements from '@common/types/artist/achievements';
import { ArtistRelation } from '@common/types/artist/artist-relation';

export class CreateArtist implements LoadingAction {
  readonly type = actions.CREATE_ARTIST;
  readonly isLoading = true;
  readonly mainLoader = true;

  constructor(public artistData: NewInstance<SimpleArtist>) {}
}

export class CreateArtistSuccess implements LoadingAction {
  readonly type = actions.CREATE_ARTIST_SUCCESS;
  readonly isLoading = false;

  constructor(public artist: SimpleArtist) {}
}

export class CreateArtistError implements ErrorAction {
  readonly type = actions.CREATE_ARTIST_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateArtist implements LoadingAction {
  readonly type = actions.UPDATE_ARTIST;
  readonly isLoading = true;

  constructor(public artist: SimpleArtist) {}
}

export class UpdateArtistSuccess implements LoadingAction {
  readonly type = actions.UPDATE_ARTIST_SUCCESS;
  readonly isLoading = false;

  constructor(public artist: SimpleArtist) {}
}

export class UpdateArtistError implements ErrorAction {
  readonly type = actions.UPDATE_ARTIST_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateArtistData implements LoadingAction {
  readonly type = actions.UPDATE_ARTIST_DATA;
  readonly isLoading = true;

  constructor(public id: string, public artist: Partial<SimpleArtist>) {}
}

export class GetArtists implements LoadingAction {
  readonly type = actions.GET_ARTISTS;
  readonly isLoading = true;
  readonly mainLoader = true;
}

export class GetArtistsSuccess implements LoadingAction {
  readonly type = actions.GET_ARTISTS_SUCCESS;
  readonly isLoading = false;

  constructor(public artists: SimpleArtist[]) {}
}

export class GetArtistsError implements ErrorAction {
  readonly type = actions.GET_ARTISTS_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class DeactivateArtist implements Action {
  readonly type = actions.DEACTIVATE_ARTIST;

  constructor(public artist: SimpleArtist) {}
}

export class DeactivateArtistSuccess implements LoadingAction {
  readonly type = actions.DEACTIVATE_ARTIST_SUCCESS;
  readonly isLoading = false;

  constructor(public artist: SimpleArtist) {}
}

export class DeactivateArtistError implements ErrorAction {
  readonly type = actions.DEACTIVATE_ARTIST_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class RestoreDeactivatedArtist implements Action {
  readonly type = actions.RESTORE_DEACTIVATED_ARTIST;
  readonly isLoading = true;

  constructor(public artistId: string, public releaseIds?: string[]) {}
}

export class RestoreDeactivatedArtistSuccess implements LoadingAction {
  readonly type = actions.RESTORE_DEACTIVATED_ARTIST_SUCCESS;
  readonly isLoading = false;
}

export class RestoreDeactivatedArtistError implements ErrorAction {
  readonly type = actions.RESTORE_DEACTIVATED_ARTIST_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class GetArtistsTeam implements LoadingAction {
  readonly type = actions.GET_ARTIST_TEAM;
  readonly isLoading = true;

  constructor(public artistId: string) {}
}

export class GetArtistTeamSuccess implements LoadingAction {
  readonly type = actions.GET_ARTIST_TEAM_SUCCESS;
  readonly isLoading = false;

  constructor(public team: SimpleUser[]) {}
}

export class GetArtistTeamError implements ErrorAction {
  readonly type = actions.GET_ARTIST_TEAM_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class RemoveFromArtistTeam implements LoadingAction {
  readonly type = actions.REMOVE_FROM_ARTIST_TEAM;
  readonly isLoading = true;
  readonly mainLoader = true;

  constructor(public artistId: string, public userId: string) {}
}

export class RemoveFromArtistTeamSuccess implements LoadingAction {
  readonly type = actions.REMOVE_FROM_ARTIST_TEAM_SUCCESS;
  readonly isLoading = false;

  constructor(public artistId: string) {}
}

export class RemoveFromArtistTeamError implements ErrorAction {
  readonly type = actions.REMOVE_FROM_ARTIST_TEAM_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class SwitchArtistTeamRole implements LoadingAction {
  readonly type = actions.SWITCH_TEAM_MEMBER_ROLE;
  readonly isLoading = true;
  readonly mainLoader = true;

  constructor(
    public artistId: string,
    public teamMemberId: string,
    public role: ArtistRelation['role']
  ) {}
}

export class SwitchArtistTeamRoleSuccess implements LoadingAction {
  readonly type = actions.SWITCH_TEAM_MEMBER_ROLE_SUCCESS;
  readonly isLoading = false;

  constructor(
    public artistId: string,
    public teamMemberId: string,
    public role: ArtistRelation['role']
  ) {}
}

export class SwitchArtistTeamRoleError implements ErrorAction {
  readonly type = actions.SWITCH_TEAM_MEMBER_ROLE_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class SearchArtists implements LoadingAction {
  readonly type = actions.SEARCH_ARTISTS;
  readonly isLoading = true;

  constructor(public query: string) {}
}

export class SearchArtistsSuccess implements LoadingAction {
  readonly type = actions.SEARCH_ARTISTS_SUCCESS;
  readonly isLoading = false;

  constructor(public artists: SimpleArtist[]) {}
}

export class SearchArtistsError implements ErrorAction {
  readonly type = actions.SEARCH_ARTISTS_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class SearchArtistsReset implements Action {
  readonly type = actions.SEARCH_ARTISTS_RESET;
}

export class SearchExternalArtists implements LoadingAction {
  readonly type = actions.SEARCH_EXTERNAL_ARTISTS;
  readonly isLoading = true;

  constructor(public query: string) {}
}

export class SearchExternalArtistsSuccess implements LoadingAction {
  readonly type = actions.SEARCH_EXTERNAL_ARTISTS_SUCCESS;
  readonly isLoading = false;

  constructor(public results: ArtistSearchResponse) {}
}

export class SearchExternalArtistsError implements ErrorAction {
  readonly type = actions.SEARCH_EXTERNAL_ARTISTS_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class SearchExternalArtistsReset implements Action {
  readonly type = actions.SEARCH_EXTERNAL_ARTISTS_RESET;
}

export class SetActiveArtist implements Action {
  readonly type = actions.SET_ACTIVE_ARTIST;

  constructor(
    public artist: SimpleArtist,
    public onboarding: boolean = false
  ) {}
}

export class UpdatePageField<T extends keyof ArtistPage> implements Action {
  readonly type = actions.UPDATE_PAGE_FIELD;

  constructor(public field: T, public value: ArtistPage[T]) {}
}

export class UpdatePageSectionField<
  T extends ArtistPageSection,
  U extends keyof ArtistPage[T]
> implements Action
{
  readonly type = actions.UPDATE_PAGE_SECTION_FIELD;

  constructor(
    public section: T,
    public field: U,
    public value: ArtistPage[T][U]
  ) {}
}

export class RemoveProfileImage implements LoadingAction {
  readonly type = actions.REMOVE_PROFILE_IMAGE;
  readonly isLoading = true;

  constructor(public id: string) {}
}

export class RemoveProfileImageSuccess implements LoadingAction {
  readonly type = actions.REMOVE_PROFILE_IMAGE_SUCCESS;
  readonly isLoading = false;

  constructor(public id: string) {}
}

export class RemoveProfileImageError implements ErrorAction {
  readonly type = actions.REMOVE_PROFILE_IMAGE_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateArtistPage implements LoadingAction {
  readonly type = actions.UPDATE_ARTIST_PAGE;
  readonly isLoading = true;
}

export class UpdateArtistPageSuccess implements LoadingAction {
  readonly type = actions.UPDATE_ARTIST_PAGE_SUCCESS;
  readonly isLoading = false;
}

export class UpdateArtistPageError implements ErrorAction {
  readonly type = actions.UPDATE_ARTIST_PAGE_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class GetArtistPage implements LoadingAction {
  readonly type = actions.GET_ARTIST_PAGE;
  readonly isLoading = true;
  readonly mainLoader = true;
}

export class GetArtistPageSuccess implements LoadingAction {
  readonly type = actions.GET_ARTIST_PAGE_SUCCESS;
  readonly isLoading = false;

  constructor(public page: ArtistPage) {}
}

export class GetArtistPageError implements ErrorAction {
  readonly type = actions.GET_ARTIST_PAGE_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}
export class GetAchievements implements LoadingAction {
  readonly type = actions.GET_ACHIEVEMENTS;
  readonly isLoading = true;
}

export class GetAchievementsSuccess implements LoadingAction {
  readonly type = actions.GET_ACHIEVEMENTS_SUCCESS;
  readonly isLoading = false;

  constructor(public achievements: Achievements[]) {}
}

export class GetAchievementsError implements ErrorAction {
  readonly type = actions.GET_ACHIEVEMENTS_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateAchievement implements LoadingAction {
  readonly type = actions.UPDATE_ACHIEVEMENT;
  readonly isLoading = true;

  constructor(public artistId: string, public achievementId: string) {}
}

export class UpdateAchievementSuccess implements LoadingAction {
  readonly type = actions.UPDATE_ACHIEVEMENT_SUCCESS;
  readonly isLoading = false;
}

export class UpdateAchievementError implements ErrorAction {
  readonly type = actions.UPDATE_ACHIEVEMENT_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class PublishArtistPage implements LoadingAction {
  readonly type = actions.PUBLISH_ARTIST_PAGE;
  readonly isLoading = true;
  readonly mainLoader = true;

  constructor(public page: ArtistPage) {}
}

export class PublishArtistPageSuccess implements LoadingAction {
  readonly type = actions.PUBLISH_ARTIST_PAGE_SUCCESS;
  readonly isLoading = false;
}

export class PublishArtistPageError implements ErrorAction {
  readonly type = actions.PUBLISH_ARTIST_PAGE_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class ResetArtistStore implements Action {
  readonly type = actions.RESET_ARTIST_STORE;
}

export interface ArtistInviteSource {
  assetType: ValidAssetType;
  type: ValidType;
  id?: string;
}

export class CreateArtistInvite implements LoadingAction {
  readonly type = actions.CREATE_ARTIST_INVITE;
  readonly isLoading = true;

  constructor(
    public invite: InviteArtistRequest,
    public source?: ArtistInviteSource
  ) {}
}

export class CreateArtistInviteSuccess implements LoadingAction {
  readonly type = actions.CREATE_ARTIST_INVITE_SUCCESS;
  readonly isLoading = false;

  constructor(
    public artist: SimpleArtist,
    public role: string,
    public source?: ArtistInviteSource
  ) {}
}

export class CreateArtistInviteError implements ErrorAction {
  readonly type = actions.CREATE_ARTIST_INVITE_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class UpdateArtistsPlatforms implements LoadingAction {
  readonly type = actions.UPDATE_ARTISTS_PLATFORMS;
  readonly isLoading = true;

  constructor(
    public artists: SimpleArtist[],
    public ownerId?: string,
    public releaseId?: string
  ) {}
}

export class UpdateArtistsPlatformsSuccess implements LoadingAction {
  readonly type = actions.UPDATE_ARTISTS_PLATFORMS_SUCCESS;
  readonly isLoading = false;

  constructor(
    public artists: SimpleArtist[],
    public ownerId?: string,
    public releaseId?: string
  ) {}
}

export class UpdateArtistsPlatformsError implements ErrorAction {
  readonly type = actions.UPDATE_ARTISTS_PLATFORMS_ERROR;
  readonly isError = true;

  constructor(public code: string) {}
}

export class ValidateExternalArtistId implements LoadingAction {
  readonly type = actions.VALIDATE_EXTERNAL_ARTIST_ID;
  readonly isLoading = true;

  constructor(public externalId: string) {}
}

export class ValidateExternalArtistIdSuccess implements Action {
  readonly type = actions.VALIDATE_EXTERNAL_ARTIST_ID_SUCCESS;

  constructor(public externalId: string, public validationResult: boolean) {}
}

export class ValidateExternalArtistIdError implements Action {
  readonly type = actions.VALIDATE_EXTERNAL_ARTIST_ID_ERROR;

  constructor(public externalId: string) {}
}

export type ArtistActions =
  | SetActiveArtist
  | CreateArtist
  | CreateArtistSuccess
  | CreateArtistError
  | DeactivateArtist
  | DeactivateArtistSuccess
  | DeactivateArtistError
  | RestoreDeactivatedArtist
  | RestoreDeactivatedArtistSuccess
  | RestoreDeactivatedArtistError
  | GetArtistsTeam
  | GetArtistTeamSuccess
  | GetArtistTeamError
  | RemoveFromArtistTeam
  | RemoveFromArtistTeamSuccess
  | RemoveFromArtistTeamError
  | SwitchArtistTeamRole
  | SwitchArtistTeamRoleSuccess
  | SwitchArtistTeamRoleError
  | UpdateArtist
  | UpdateArtistSuccess
  | UpdateArtistError
  | GetArtists
  | GetArtistsSuccess
  | GetArtistsError
  | SearchArtists
  | SearchArtistsSuccess
  | SearchArtistsError
  | SearchArtistsReset
  | SearchExternalArtists
  | SearchExternalArtistsSuccess
  | SearchExternalArtistsError
  | SearchExternalArtistsReset
  | UpdatePageField<keyof ArtistPage>
  | UpdatePageSectionField<ArtistPageSection, never>
  | RemoveProfileImage
  | RemoveProfileImageSuccess
  | RemoveProfileImageError
  | GetArtistPage
  | GetArtistPageSuccess
  | GetArtistPageError
  | UpdateArtistPage
  | UpdateArtistPageSuccess
  | UpdateArtistPageError
  | PublishArtistPage
  | PublishArtistPageSuccess
  | PublishArtistPageError
  | ResetArtistStore
  | CreateArtistInvite
  | CreateArtistInviteSuccess
  | CreateArtistInviteError
  | UpdateArtistData
  | UpdateArtistsPlatforms
  | UpdateArtistsPlatformsSuccess
  | UpdateArtistsPlatformsError
  | GetAchievements
  | GetAchievementsSuccess
  | GetAchievementsError
  | UpdateAchievement
  | UpdateAchievementSuccess
  | UpdateAchievementError
  | ValidateExternalArtistId
  | ValidateExternalArtistIdSuccess
  | ValidateExternalArtistIdError;
